import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Camera, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { couponStore, lookupPreview, normTarga } from "@/lib/api";
import FreePreview from "./FreePreview";
import Paywall from "./Paywall";
import CameraScanner from "./CameraScanner";

export default function SearchCard() {
  const [targa, setTarga] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [coupon, setCoupon] = useState(() => couponStore.get());

  const submit = async (e) => {
    e?.preventDefault();
    const t = normTarga(targa);
    if (t.length < 5) {
      toast.error("Inserisci una targa valida prima di procedere.");
      return;
    }
    setLoading(true);
    try {
      const data = await lookupPreview(t);
      setPreview(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Targa non valida o servizio non disponibile.");
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="vt-card" data-testid="search-card">
      <form onSubmit={submit} className="space-y-5">
        <label
          htmlFor="targa"
          className="block font-orbitron text-[11px] font-bold uppercase tracking-[0.25em] text-[#8a8a9e]"
        >
          Targa Veicolo
        </label>
        <div className="relative">
          <input
            data-testid="plate-input-field"
            id="targa"
            value={targa}
            onChange={(e) => setTarga(e.target.value.toUpperCase().slice(0, 8))}
            placeholder="ES. GW908GP"
            maxLength={8}
            autoComplete="off"
            className="w-full px-4 py-4 bg-[#050507] border border-white/10 rounded-xl text-white text-lg font-orbitron tracking-[0.3em] text-center uppercase focus:outline-none focus:border-[#ff007f] focus:shadow-[0_0_18px_rgba(255,0,127,0.35)] transition-all placeholder:text-white/20 placeholder:tracking-[0.2em]"
          />
          {loading && (
            <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none">
              <div className="absolute inset-y-0 w-20 bg-gradient-to-r from-transparent via-[#00f0ff]/30 to-transparent animate-scan" />
            </div>
          )}
        </div>
        <button
          data-testid="plate-search-submit-button"
          type="submit"
          disabled={loading}
          className="w-full bg-[#ff007f] hover:bg-[#e00070] disabled:opacity-60 text-white py-4 rounded-xl font-orbitron font-bold text-sm tracking-[0.15em] uppercase shadow-[0_0_20px_rgba(255,0,127,0.35)] hover:shadow-[0_0_32px_rgba(255,0,127,0.55)] hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 cursor-pointer"
        >
          {loading ? (
            <>
              <Loader2 size={16} className="animate-spin" /> SCANSIONE IN CORSO...
            </>
          ) : (
            <>
              VERIFICA VEICOLO <ArrowRight size={16} />
            </>
          )}
        </button>
        <button
          type="button"
          data-testid="camera-scanner-open-button"
          onClick={() => setScannerOpen(true)}
          className="w-full border border-[#00f0ff]/40 text-[#00f0ff] py-3 rounded-xl font-orbitron text-[11px] font-bold tracking-[0.2em] uppercase hover:bg-[#00f0ff]/10 transition-all flex items-center justify-center gap-2 cursor-pointer"
        >
          <Camera size={15} /> SCANDISCI CON LA FOTOCAMERA
        </button>
      </form>

      <CameraScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onResult={(t) => setTarga(t)}
      />

      <div className="flex flex-wrap items-center gap-2 mt-4 font-jetmono text-[10px] text-[#8a8a9e]">
        <span className="uppercase tracking-[0.2em]">Prova con:</span>
        {["GW908GP", "FA482KP"].map((p) => (
          <button
            key={p}
            type="button"
            data-testid="sample-plate-chip"
            onClick={() => setTarga(p)}
            className="px-2.5 py-1 rounded-md border border-[#00f0ff]/30 text-[#00f0ff] hover:bg-[#00f0ff]/10 transition-colors font-jetmono text-[11px] tracking-[0.2em] cursor-pointer"
          >
            {p}
          </button>
        ))}
      </div>

      {coupon && (
        <div
          className="flex items-center justify-between gap-3 mt-4 rounded-lg border border-[#00ff88]/40 bg-[#00ff88]/10 px-3 py-2"
          data-testid="coupon-active-chip"
        >
          <span className="font-jetmono text-[10px] tracking-[0.2em] text-[#00ff88]">
            COUPON ATTIVO: {coupon}
          </span>
          <button
            type="button"
            data-testid="coupon-remove-button"
            onClick={() => {
              couponStore.clear();
              setCoupon(null);
              toast.info("Coupon rimosso.");
            }}
            className="text-[#8a8a9e] hover:text-white transition-colors cursor-pointer"
            aria-label="Rimuovi coupon"
          >
            <X size={14} />
          </button>
        </div>
      )}

      <AnimatePresence mode="wait">
        {preview && (
          <motion.div
            key={preview.targa}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            data-testid="preview-result-area"
          >
            <FreePreview preview={preview} />
            <Paywall targa={preview.targa} unlocked={preview.unlocked} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
